import { useEffect } from 'react';

const SHIMMER_KEYFRAMES = `
@keyframes skeletonShimmer {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
`;

const shimmerStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '800px 100%',
  animation: 'skeletonShimmer 1.5s ease-in-out infinite',
  borderRadius: '6px',
};

const line = (width: string, height = '1rem', mb = '0'): React.CSSProperties => ({
  ...shimmerStyle,
  width,
  height,
  marginBottom: mb,
});

interface LandingPageSkeletonProps {
  isMobile?: boolean;
}

const LandingPageSkeleton = ({ isMobile = false }: LandingPageSkeletonProps) => {
  const isLargeDesktop = !isMobile && window.innerWidth >= 1920;

  useEffect(() => {
    if (document.getElementById('skeleton-shimmer-style')) return;
    const style = document.createElement('style');
    style.id = 'skeleton-shimmer-style';
    style.textContent = SHIMMER_KEYFRAMES;
    document.head.appendChild(style);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0', width: '100%' }}>
      {/* Title skeleton — fontSize: isMobile ? 1.9rem : 2.1rem, margin: 0 0 0.5rem 0 */}
      <div style={{ marginBottom: '0.5rem' }}>
        <div style={line('75%', isMobile ? '1.9rem' : '2.1rem', '0.5rem')} />
        <div style={line('55%', isMobile ? '1.9rem' : '2.1rem')} />
      </div>

      {/* Description skeleton — fontSize: isMobile ? 0.95rem : 1.1rem, margin: 0 0 1.5rem 0 */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={line('100%', isMobile ? '0.95rem' : '1.1rem', '0.45rem')} />
        <div style={line('90%', isMobile ? '0.95rem' : '1.1rem', '0.45rem')} />
        <div style={line('40%', isMobile ? '0.95rem' : '1.1rem')} />
      </div>

      {/* Stats row skeleton — gap: isMobile ? 1rem : 1.5rem, marginBottom: 2rem, fontSize: 0.9rem */}
      <div style={{ display: 'flex', gap: isMobile ? '1rem' : '1.5rem', marginBottom: '2rem', marginLeft: '0.25rem' }}>
        <div style={line('8rem', '0.9rem')} />
        <div style={line('10rem', '0.9rem')} />
      </div>

      {/* Job info card skeleton — borderRadius: 14px, padding: 0.85rem 1rem, marginBottom: 1.75rem */}
      <div style={{
        border: '1px solid #DEE2E6',
        borderRadius: '14px',
        padding: '0.85rem 1rem',
        marginBottom: '1.75rem',
        backgroundColor: '#F2F3F7',
      }}>
        <div style={line('60%', '1.3rem', '0.6rem')} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <div style={{ ...shimmerStyle, width: '1.05rem', height: '1.05rem', borderRadius: '50%', flexShrink: 0 }} />
          <div style={line('40%', '0.9rem')} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ ...shimmerStyle, width: '1.05rem', height: '1.05rem', borderRadius: '50%', flexShrink: 0 }} />
          <div style={line('55%', '0.9rem')} />
        </div>
      </div>

      {/* Interview Preparation Tips skeleton — marginBottom: isLargeDesktops ? 9rem : 6rem */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: isLargeDesktop ? '9rem' : '6rem', marginLeft: '0.25rem' }}>
        <div style={{ ...shimmerStyle, width: '1.05rem', height: '1.05rem', borderRadius: '50%', flexShrink: 0 }} />
        <div style={line('11rem', '0.9rem')} />
      </div>

      {/* Terms checkbox skeleton — fontSize: 0.85rem, marginBottom: 1rem */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.3rem', marginBottom: '1rem' }}>
        <div style={{ ...shimmerStyle, width: '1.1rem', height: '1.1rem', borderRadius: '4px', flexShrink: 0, marginTop: '2px' }} />
        <div style={{ flex: 1 }}>
          <div style={line('100%', '0.85rem', '0.35rem')} />
          <div style={line('70%', '0.85rem')} />
        </div>
      </div>

      {/* Get Started button skeleton — padding: 0.8rem, borderRadius: 10px, marginBottom: 0.8rem */}
      <div style={{ ...shimmerStyle, width: '100%', height: '2.8rem', borderRadius: '10px', marginBottom: '0.8rem' }} />

      {/* Watch Tutorial Video skeleton — gap: 0.6rem, padding: 0.3rem, fontSize: 0.95rem */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.6rem', padding: '0.3rem 0' }}>
        <div style={{ ...shimmerStyle, width: '1.05rem', height: '1.05rem', borderRadius: '50%', flexShrink: 0 }} />
        <div style={line('9rem', '0.95rem')} />
      </div>
    </div>
  );
};

export default LandingPageSkeleton;
